import type { TypedDocumentNode } from '@apollo/client'
import { gql, NetworkStatus } from '@apollo/client'
import { QueryRef, useApolloClient, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { Card, CardContent, CardHeader, CardTitle } from '@repo/shared-client/components/ui'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import Albums from 'src/features/album/albums/albums'
import { MediaCardSelectProps } from 'src/features/media/media-card/media-card'
import MediasAlbum from 'src/features/media/medias-album/medias-album'
import ProfileHeader from 'src/features/profile/profile-header/profile-header'
import ProfileMenu from 'src/features/profile/profile-menu/profile-menu'
import ProfileTaglineCard from 'src/features/profile/profile-tagline-card/profile-tagline-card'
import { ProfileInfo, ProfileMediaInfo } from 'src/lib/fragments'
import type { FindProfileMediaQuery, FindProfileMediaQueryVariables } from 'types/graphql'

const PROFILE_MEDIA_QUERY: TypedDocumentNode<FindProfileMediaQuery, FindProfileMediaQueryVariables> = gql`
  query FindProfileMediaQuery($profileId: String!) {
    isBlocked(profileId: $profileId)
    isFriend(profileId: $profileId) {
      id
      friendId
      approved
    }
    isFollowing(profileId: $profileId) {
      id
      followingId
    }
    friendsCounts(filter: { profileId: $profileId }) {
      friends
      pending
      requests
    }
    profile(id: $profileId) {
      ...ProfileInfo
      albums {
        id
        name
        deletable
        privacy
      }
    }
  }
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

export const Route = createFileRoute('/(auth)/(main-layout)/profile/$profileId_/media/')({
  component: MediaPage,
  head: () =>
    seo({
      title: `Profile Media | ${env.VITE_APP_NAME}`,
      description: 'Profile media page',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, PROFILE_MEDIA_QUERY, {
      variables: { profileId: params.profileId },
    })
    return { queryRef }
  },
})

function MediaPage() {
  const { t } = useTranslation()
  const { queryRef } = Route.useLoaderData()
  const apolloClient = useApolloClient()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<FindProfileMediaQuery>)
  const { albumId } = useSearch({ strict: false })
  const navigate = useNavigate()

  const handleRefetch = async () => {
    await apolloClient.refetchQueries({ include: [PROFILE_MEDIA_QUERY] })
  }

  const isInitialLoading = networkStatus === NetworkStatus.loading

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!data?.profile) {
    return <ErrorAlert error={{ name: 'Empty', message: t('Profile Not Found') }} />
  }

  const { profile, isBlocked, isFriend, isFollowing } = data

  const publicAlbum = profile?.albums.find(
    (album) => album.name.toLowerCase() === 'public' && album.privacy === 'PUBLIC'
  )

  const defaultAlbum = publicAlbum ? publicAlbum.id : profile?.albums[0]?.id

  const currentAlbum = albumId ? albumId : defaultAlbum

  const handleSelectAlbum = (id: string) => {
    navigate({
      to: '/profile/$profileId/media',
      params: { profileId: profile.id },
      search: { albumId: id },
      replace: true,
      resetScroll: false,
    } as never)
  }

  const handleMediaAlbum = (media: MediaCardSelectProps) => {
    navigate({
      to: '/media/$mediaId',
      params: { mediaId: media.id },
      search: { albumId: currentAlbum },
    } as never)
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-0">
      <ProfileHeader
        isBlocked={isBlocked}
        isFollowing={isFollowing}
        isFriend={isFriend}
        onQueryRefetch={handleRefetch}
        profile={profile}
      />
      <div className="mb-4 grid grid-cols-2 gap-3 px-2 md:grid-cols-4">
        <div className="col-span-2 hidden md:col-span-1 md:mt-6 md:flex md:w-full md:justify-center">
          <ProfileMenu
            className="hidden w-[80%] max-w-full md:flex"
            isBlocked={isBlocked}
            onQueryRefetch={handleRefetch}
            profile={profile}
          />
        </div>
        <div className="col-span-2 mt-10 md:col-span-2 md:mt-0">
          <div className="flex w-full flex-col items-stretch gap-4">
            <ProfileTaglineCard profile={profile} />
            <Card className="w-full">
              <CardHeader>
                <CardTitle>{t('Albums')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Albums onSelectAlbum={handleSelectAlbum} profileId={profile?.id} />
              </CardContent>
            </Card>
            <Card className="w-full">
              <CardHeader>
                <CardTitle>{t('Media')}</CardTitle>
              </CardHeader>
              <CardContent>
                <MediasAlbum albumId={currentAlbum} onSelectMedia={handleMediaAlbum} profileId={profile?.id} />
              </CardContent>
            </Card>
          </div>
        </div>
        <div className="col-span-1 hidden md:block" />
      </div>
    </div>
  )
}
